import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';

import { AppState } from '@cpd-app/state';
import {
  selectLoading,
  selectIssues,
  selectSystemReports,
  selectFeedbacks,
  selectAnalytics,
  selectAllSystemsOperational,
  selectSystemReportsUnavailable
} from './system-status.selectors';
import {
  getIssues,
  reportBug,
  reportBugSuccess,
  feedback,
  feedbackSuccess,
  getSystemReports,
  createSystemStatusReport,
  togglePublishAllSystemStatusReport,
  deleteSystemStatusReport,
  togglePublishAllSystemStatusReportSuccess,
  createSystemStatusReportSuccess,
  markBugReportsViewed,
  getFeedbacks,
  getFeedbackAnalytics
} from './system-status.actions';
import {
  BugReportInput,
  BugReportViewedInput,
  FeedbackInput,
  SystemStatusReportInput,
  TogglePublishAllSystemStatusReportInput
} from '@cpd-app/generated';
import { Actions, ofType } from '@ngrx/effects';
import { map } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SystemStatusFacade {
  public readonly issues$ = this.store.pipe(select(selectIssues));
  public readonly numOfNewIssues$ = this.issues$.pipe(
    map((issues) => issues.filter((item) => !item.isviewed)?.length)
  );
  public readonly feedbacks$ = this.store.pipe(select(selectFeedbacks));
  public readonly analytics$ = this.store.pipe(select(selectAnalytics));

  public readonly systemReports$ = this.store.pipe(select(selectSystemReports));

  /** True only when loading the reports failed. See the selectors for why. */
  public readonly systemReportsUnavailable$ = this.store.pipe(
    select(selectSystemReportsUnavailable)
  );

  /** True only once a successful load has confirmed there are no open issues. */
  public readonly allSystemsOperational$ = this.store.pipe(
    select(selectAllSystemsOperational)
  );
  public readonly publishedSystemReports$ = this.systemReports$.pipe(
    map((reports) => reports.filter((item) => item.ispublished))
  );
  public readonly loading$ = this.store.pipe(select(selectLoading));
  public readonly reportBugSuccess$ = this.actions$.pipe(
    ofType(reportBugSuccess)
  );
  public readonly feedbackSuccess$ = this.actions$.pipe(
    ofType(feedbackSuccess)
  );

  public readonly togglePublishAllSystemStatusReportSuccess$ =
    this.actions$.pipe(ofType(togglePublishAllSystemStatusReportSuccess));

  public readonly createSystemStatusReportSuccess$ = this.actions$.pipe(
    ofType(createSystemStatusReportSuccess)
  );

  constructor(
    private store: Store<AppState>,
    private actions$: Actions
  ) {}

  public getIssues(): void {
    this.store.dispatch(getIssues());
  }

  public getFeedbacks(): void {
    this.store.dispatch(getFeedbacks());
  }

  public getFeedbackAnalytics(): void {
    this.store.dispatch(getFeedbackAnalytics());
  }

  public getSystemReports(): void {
    this.store.dispatch(getSystemReports());
  }

  public reportBug(input: BugReportInput): void {
    this.store.dispatch(reportBug({ input }));
  }

  public feedback(input: FeedbackInput): void {
    this.store.dispatch(feedback({ input }));
  }

  public createSystemStatusReport(input: SystemStatusReportInput): void {
    this.store.dispatch(createSystemStatusReport({ input }));
  }

  public deleteSystemStatusReport(ids: string[]): void {
    if (!ids.length) {
      return;
    }
    this.store.dispatch(deleteSystemStatusReport({ ids }));
  }

  public togglePublishAllSystemStatusReport(
    input: TogglePublishAllSystemStatusReportInput
  ): void {
    this.store.dispatch(togglePublishAllSystemStatusReport({ input }));
  }

  public markBugReportsViewed(input: BugReportViewedInput): void {
    this.store.dispatch(markBugReportsViewed({ input }));
  }
}
